import React, { useCallback, useEffect, useRef, useState } from 'react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import ResyncFields, { kindDefOf, filledRangesOf, anyBadOrderOf } from './ResyncFields';
import '../shared/ConfirmModal.css';

// RESYNC AN OHIP ADAPTER PIPELINE (V705) — the modal the Resync button opens on a pipeline implementation whose source
// system names a Polling Implementation (admin › Marketplace › Adapter). It is modelled on OPERA's own resync screen:
//
//   1. Kind     Reservations, Profiles or Blocks — one at a time, as OPERA does.
//   2. Dates    the date ranges that kind offers (ResyncFields) — leave a kind blank to skip it.
//   3. Run      the source system's resync Step Functions state machine runs for this one implementation, and this
//               modal polls its progress — which step it is on, and how many records it has sent — from OpenSearch.
//
// PREVIOUS RESYNCS of this implementation are listed under the form (the run documents in OpenSearch), each expandable to
// its steps and its new/updated/unchanged breakdown, so a past run can be reviewed without leaving the modal.
//
// The kind + date selection is shared with the BulkResyncModal (ResyncFields), so both offer the same searches.

const TERMINAL = ['done', 'failed'];
const POLL_MS = 2500;
const stepDot = (status) => (status === 'done' ? '#16a34a' : status === 'running' ? '#2563eb' : status === 'failed' ? '#dc2626' : '#d1d5db');
const statusColor = (status) => (status === 'done' ? '#047857' : status === 'failed' ? '#dc2626' : '#2563eb');
const whenLabel = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '');
// A run's criteria in one line: "Reservations · updated 2026-10-01→2026-10-31".
const rangesSummary = (run) => {
    if (run.byStatus && run.byStatus.status) return `${run.type || 'resync'} · ${run.byStatus.status}, last ${run.byStatus.days}d`;
    const rs = run.ranges || {};
    const parts = Object.keys(rs).filter(k => rs[k] && rs[k].from && rs[k].to).map(k => `${k} ${rs[k].from}→${rs[k].to}`);
    return `${run.type || 'resync'}${parts.length ? ` · ${parts.join(', ')}` : ''}`;
};

// A run's steps and its counts — shown live while a resync runs and in the history detail of a past one.
function RunProgress({ run }) {
    if (!run) return null;
    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', margin: '0.5rem 0 0.75rem' }}>
                {(run.steps || []).map(s => (
                    <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: stepDot(s.status), flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, color: '#111827' }}>{s.label}</span>
                        <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>{s.detail || ''}</span>
                    </div>
                ))}
            </div>
            {run.sent != null && (
                <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                    {run.sent} sent{run.failed ? `, ${run.failed} failed` : ''}{run.total ? ` of ${run.total}` : ''}.
                </div>
            )}
            {/* EVERY selected record is resent; this is only HOW each compared to the last time this pipeline sent it
                (the delivery ledger — reservations and profiles), not what was or wasn't sent. */}
            {(run.count_new || run.count_updated || run.count_unchanged) ? (
                <>
                    <div style={{ display: 'flex', gap: '1.25rem', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                        <span><strong style={{ color: '#047857' }}>{run.count_new || 0}</strong> new to the target</span>
                        <span><strong style={{ color: '#b45309' }}>{run.count_updated || 0}</strong> changed since last sent</span>
                        <span><strong style={{ color: '#6b7280' }}>{run.count_unchanged || 0}</strong> unchanged since last sent</span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '0.3rem' }}>
                        All {run.sent != null ? run.sent : 0} were resent — this is just how each compared to the last time this pipeline sent it.
                    </div>
                </>
            ) : null}
            {run.note && <div style={{ fontSize: '0.85rem', color: '#b45309', marginTop: '0.4rem' }}>{run.note}</div>}
            {run.error && <div style={{ fontSize: '0.85rem', color: '#dc2626', marginTop: '0.4rem' }}>{run.error}</div>}
        </>
    );
}

export default function ResyncModal({ templateId, implementationId, sourceName, onClose }) {
    const [kind, setKind] = useState('reservations');
    const [ranges, setRanges] = useState({});               // { [rangeKey]: { from, to } }
    const [status, setStatus] = useState('');               // resync by log status instead of a date range
    const [days, setDays] = useState(7);
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [run, setRun] = useState(null);                   // the status document once started
    const [runId, setRunId] = useState(null);
    const [history, setHistory] = useState(null);           // null = not loaded; [] = none; previous runs otherwise
    const [expandedId, setExpandedId] = useState(null);
    const timer = useRef(null);

    const kindDef = kindDefOf(kind);
    const available = kindDef.available !== false;
    // Only the ranges the chosen kind offers, both ends filled, go to the server.
    const filledRanges = filledRangesOf(kind, ranges);
    const anyRange = Object.keys(filledRanges).length > 0;
    const anyBadOrder = anyBadOrderOf(kind, ranges);

    const setRange = (rk, end, value) => setRanges(prev => ({ ...prev, [rk]: { ...prev[rk], [end]: value } }));

    const stop = useCallback(() => { if (timer.current) { clearInterval(timer.current); timer.current = null; } }, []);
    useEffect(() => stop, [stop]);

    const loadHistory = useCallback(async () => {
        try {
            const res = await integrationsService.listResyncRuns(templateId, implementationId);
            setHistory(res.runs || []);
        } catch (e) {
            setHistory([]);
        }
    }, [templateId, implementationId]);
    useEffect(() => { loadHistory(); }, [loadHistory]);

    const poll = useCallback(async (id) => {
        try {
            const res = await integrationsService.getResyncStatus(templateId, implementationId, id);
            const r = res.run || res;
            setRun(r);
            if (r && TERMINAL.includes(r.status)) stop();
        } catch (e) {
            // A transient poll failure is not the run failing — keep the last state and let the next tick retry.
        }
    }, [templateId, implementationId, stop]);

    const start = async () => {
        setError(null);
        if (!available) { setError('Block resync is not available for this source yet.'); return; }
        // By status wins when it is set — every record of this kind with that status in the window; else the date ranges.
        const body = status ? { type: kind, status, days } : { type: kind, ranges: filledRanges };
        if (!status) {
            if (!anyRange) { setError('Enter at least one date range (a from and a to), or choose a status.'); return; }
            if (anyBadOrder) { setError('Each range’s From must be on or before its To.'); return; }
        }
        setStarting(true);
        try {
            const res = await integrationsService.startResync(templateId, implementationId, body);
            setRunId(res.runId);
            await poll(res.runId);
            timer.current = setInterval(() => poll(res.runId), POLL_MS);
        } catch (e) {
            setError(apiErrorMessage(e, 'The resync could not be started.'));
        } finally {
            setStarting(false);
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const started = Boolean(runId);
    const done = run && TERMINAL.includes(run.status);

    // When the run finishes, refresh the history so the run just completed appears in it.
    useEffect(() => { if (done) loadHistory(); }, [done, loadHistory]);

    // PREVIOUS RESYNCS — review a past run's criteria, outcome and breakdown. Shown before a run (the form) and after one
    // finishes (below the result), so the history is reachable either way; hidden only while a run is actively going.
    const historyBlock = (
        <div style={{ marginTop: '1.25rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Previous resyncs</div>
            {history === null ? (
                <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>Loading…</div>
            ) : history.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>No resyncs run yet.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 240, overflowY: 'auto' }}>
                    {history.map(h => (
                        <div key={h.runId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <button type="button" onClick={() => setExpandedId(expandedId === h.runId ? null : h.runId)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', textAlign: 'left',
                                    background: 'none', border: 'none', padding: '0.5rem 0.25rem', cursor: 'pointer' }}>
                                <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: stepDot(h.status) }} />
                                <span style={{ fontSize: '0.8rem', color: '#6b7280', minWidth: 116 }}>{whenLabel(h.startedAt || h.createdAt)}</span>
                                <span style={{ fontSize: '0.85rem', color: '#111827', flex: 1 }}>{rangesSummary(h)}</span>
                                <span style={{ fontSize: '0.8rem', color: statusColor(h.status), whiteSpace: 'nowrap' }}>
                                    {h.status === 'done' ? `${h.sent || 0}${h.total ? `/${h.total}` : ''} sent` : h.status}
                                </span>
                            </button>
                            {expandedId === h.runId && (
                                <div style={{ padding: '0 0.25rem 0.75rem 1.5rem' }}>
                                    {h.startedBy && <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginBottom: '0.35rem' }}>by {h.startedBy}</div>}
                                    <RunProgress run={h} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="cm-backdrop" onClick={onClose}>
            <div className="cm-dialog cm-dialog-wide" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <h2 className="cm-title">Resync{sourceName ? ` from ${sourceName}` : ''}</h2>

                {!started ? (
                    <>
                        <p className="cm-message">
                            Ask the source for a date range of records again and send them through this pipeline — the same
                            records, carried to the same targets, as if they had just happened.
                        </p>

                        <ResyncFields
                            kind={kind}
                            ranges={ranges}
                            onKind={(k) => { setKind(k); setError(null); }}
                            onRange={setRange}
                        />

                        {/* OR BY STATUS — every record of this kind that had the chosen status in the log, in the last N
                            days (e.g. every warning after a fix). Used instead of a date range when a status is set. */}
                        {available && (
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem 1rem', marginTop: '1rem' }}>
                                <div style={{ fontWeight: 600, color: '#111827', marginBottom: '0.4rem' }}>By status</div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                    Every {kindDef.label.toLowerCase()} record that had this status in the log, in the last N days — used instead of a date range.
                                </div>
                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                                        Status<br />
                                        <select value={status} onChange={e => { setStatus(e.target.value); setError(null); }}>
                                            <option value="">— none —</option>
                                            <option value="warning">warning</option>
                                            <option value="error">error</option>
                                            <option value="denied">denied</option>
                                            <option value="skipped">skipped</option>
                                        </select>
                                    </label>
                                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                                        In the last (days)<br />
                                        <input type="number" min={1} max={90} value={days} disabled={!status}
                                            onChange={e => setDays(Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 90))} />
                                    </label>
                                </div>
                            </div>
                        )}

                        {historyBlock}
                    </>
                ) : (
                    // The run's progress, polled from OpenSearch.
                    <div>
                        <p className="cm-message">
                            {done
                                ? (run.status === 'failed' ? 'The resync failed.' : 'The resync has finished.')
                                : 'The resync is running. You can leave this open to watch it, or close it — it keeps running.'}
                        </p>
                        <RunProgress run={run} />
                        {/* Once finished, the history (now including this run) is shown below the result. */}
                        {done && historyBlock}
                    </div>
                )}

                {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}

                <div className="cm-actions">
                    <button className="cm-btn-cancel" onClick={onClose}>{started && !done ? 'Close (keeps running)' : 'Close'}</button>
                    {!started && (
                        <button className="cm-btn-confirm" onClick={start} disabled={starting || !available || (!anyRange && !status)}>
                            {starting ? 'Starting…' : 'Start resync'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
